/**
 * Table of Contents Generator
 * Automatically generates and manages table of contents for blog posts
 */

class TableOfContents {
  constructor() {
    this.tocContainer = document.querySelector('.toc');
    this.articleContent = document.querySelector('article .prose');
    this.headings = [];
    this.activeHeading = null;
    this.marker = null;

    if (!this.tocContainer || !this.articleContent) {
      return;
    }

    this.init();
  }

  init() {
    this.extractHeadings();

    if (this.headings.length === 0) {
      // Hide TOC if no headings found
      const tocSidebar = this.tocContainer.closest('aside');
      if (tocSidebar) {
        tocSidebar.style.display = 'none';
      }
      return;
    }

    this.generateTOC();
    this.createMarker();
    this.setupScrollSpy();
    this.setupSmoothScroll();
  }

  extractHeadings() {
    // Add an Introduction section for content before first heading
    const firstHeading = this.articleContent.querySelector('h2, h3');
    if (firstHeading) {
      // Create a virtual introduction element at the start of the article
      const introElement = document.createElement('div');
      introElement.id = 'introduction';
      introElement.style.position = 'absolute';
      introElement.style.top = '0';
      introElement.style.height = '1px';
      introElement.style.visibility = 'hidden';
      this.articleContent.insertBefore(introElement, this.articleContent.firstChild);

      // Calculate reading time for introduction (content before first heading)
      const introContent = this.getContentBetween(this.articleContent.firstChild, firstHeading);
      const introReadTime = this.calculateReadingTime(introContent);

      this.headings.push({
        id: 'introduction',
        text: 'Introduction',
        level: 2,
        element: introElement,
        readingTime: introReadTime
      });
    }

    // Get all h2 and h3 headings from the article
    const headingElements = this.articleContent.querySelectorAll('h2, h3');

    headingElements.forEach((heading, index) => {
      // Create an ID if it doesn't exist
      if (!heading.id) {
        heading.id = this.generateId(heading.textContent);
      }

      // Calculate reading time for this section
      const nextHeading = headingElements[index + 1];
      const sectionContent = this.getContentBetween(heading, nextHeading);
      const readTime = this.calculateReadingTime(sectionContent);

      this.headings.push({
        id: heading.id,
        text: heading.textContent,
        level: parseInt(heading.tagName.substring(1)),
        element: heading,
        readingTime: readTime
      });
    });
  }

  getContentBetween(startElement, endElement) {
    let content = '';
    let current = startElement;

    while (current && current !== endElement) {
      if (current.nodeType === Node.TEXT_NODE) {
        content += current.textContent;
      } else if (current.nodeType === Node.ELEMENT_NODE) {
        content += current.textContent;
      }

      if (current.firstChild) {
        current = current.firstChild;
      } else if (current.nextSibling) {
        current = current.nextSibling;
      } else {
        // Move up the tree to find next sibling
        while (current && !current.nextSibling && current !== this.articleContent) {
          current = current.parentNode;
        }
        if (current && current !== this.articleContent) {
          current = current.nextSibling;
        } else {
          break;
        }
      }
    }

    return content;
  }

  calculateReadingTime(text) {
    const wordsPerMinute = 200;
    const words = text.trim().split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return minutes;
  }

  generateId(text) {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/--+/g, '-')
      .trim();
  }

  generateTOC() {
    const nav = document.createElement('nav');
    nav.className = 'toc-nav';

    let currentList = null;
    let currentLevel = 2;
    const listStack = [];

    this.headings.forEach((heading) => {
      const li = document.createElement('li');
      li.dataset.headingId = heading.id;

      const a = document.createElement('a');
      a.href = `#${heading.id}`;
      a.textContent = heading.text;
      a.className = 'toc-link';
      a.dataset.headingId = heading.id;

      li.appendChild(a);

      // Add reading time indicator
      if (heading.readingTime) {
        const timeSpan = document.createElement('span');
        timeSpan.className = 'toc-reading-time';
        timeSpan.textContent = ` (${heading.readingTime} min)`;
        timeSpan.style.cssText = 'color: #9ca3af; font-size: 0.85em;';
        a.appendChild(timeSpan);
      }

      // Handle nesting for h3 under h2
      if (heading.level === 2) {
        if (!currentList || heading.level < currentLevel) {
          // Create new list at level 2
          currentList = document.createElement('ol');
          currentList.className = 'toc-list toc-list-level-2';
          currentList.style.listStyleType = 'upper-roman';
          nav.appendChild(currentList);
          listStack.length = 0;
          listStack.push(currentList);
        }
        currentList.appendChild(li);
        currentLevel = 2;
      } else if (heading.level === 3) {
        // Create nested list for h3
        let parentLi = currentList ? currentList.lastElementChild : null;

        if (!parentLi) {
          // No parent h2, add to main list
          if (!currentList) {
            currentList = document.createElement('ol');
            currentList.className = 'toc-list toc-list-level-2';
            currentList.style.listStyleType = 'upper-roman';
            nav.appendChild(currentList);
            listStack.push(currentList);
          }
          currentList.appendChild(li);
        } else {
          // Check if nested list already exists
          let nestedList = parentLi.querySelector('ol');

          if (!nestedList) {
            nestedList = document.createElement('ol');
            nestedList.className = 'toc-list toc-list-level-3';
            nestedList.style.listStyleType = 'lower-roman';
            parentLi.appendChild(nestedList);
          }

          nestedList.appendChild(li);
        }
        currentLevel = 3;
      }
    });

    // Clear the placeholder comment and add the generated TOC
    this.tocContainer.innerHTML = '';
    this.tocContainer.appendChild(nav);
  }

  createMarker() {
    this.marker = document.createElement('div');
    this.marker.className = 'toc-marker';
    this.marker.innerHTML = '←';
    this.marker.style.cssText = `
      position: absolute;
      right: 0px;
      font-size: 16px;
      color: var(--color-primary, #3b82f6);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      opacity: 0;
      pointer-events: none;
      line-height: 1;
    `;

    const nav = this.tocContainer.querySelector('.toc-nav');
    if (nav) {
      nav.style.position = 'relative';
      nav.appendChild(this.marker);
    }
  }

  updateMarkerPosition(activeLink) {
    if (!this.marker || !activeLink) {
      if (this.marker) {
        this.marker.style.opacity = '0';
      }
      return;
    }

    const nav = this.tocContainer.querySelector('.toc-nav');
    const navRect = nav.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();

    const top = linkRect.top - navRect.top + nav.scrollTop;
    const height = linkRect.height;

    this.marker.style.top = `${top}px`;
    this.marker.style.height = `${height}px`;
    this.marker.style.opacity = '1';
  }

  setupScrollSpy() {
    const observerOptions = {
      rootMargin: '-100px 0px -66%',
      threshold: 0
    };

    let visibleHeadings = new Set();

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          visibleHeadings.add(entry.target.id);
        } else {
          visibleHeadings.delete(entry.target.id);
        }
      });

      // Find the first visible heading in document order
      if (visibleHeadings.size > 0) {
        for (let heading of this.headings) {
          if (visibleHeadings.has(heading.id)) {
            this.setActiveHeading(heading.id);
            break;
          }
        }
      }
    }, observerOptions);

    this.headings.forEach((heading) => {
      observer.observe(heading.element);
    });

    // Handle scroll to detect when we're at the bottom
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = document.documentElement.clientHeight;

        // If we're at the bottom, activate the last heading
        if (scrollTop + clientHeight >= scrollHeight - 10) {
          const lastHeading = this.headings[this.headings.length - 1];
          if (lastHeading) {
            this.setActiveHeading(lastHeading.id);
          }
        }
      }, 50);
    });

    // Set initial active state to first heading (introduction)
    if (this.headings.length > 0) {
      this.setActiveHeading(this.headings[0].id);
    }
  }

  setActiveHeading(id) {
    // Remove active class from all links and list items
    const allLinks = this.tocContainer.querySelectorAll('.toc-link');
    const allListItems = this.tocContainer.querySelectorAll('li');
    allLinks.forEach((link) => {
      link.classList.remove('active');
    });
    allListItems.forEach((li) => {
      li.classList.remove('selected-entry');
    });

    // Add active class to current link and selected-entry to its parent li
    const activeLink = this.tocContainer.querySelector(`.toc-link[data-heading-id="${id}"]`);
    if (activeLink) {
      activeLink.classList.add('active');
      const parentLi = activeLink.closest('li');
      if (parentLi) {
        parentLi.classList.add('selected-entry');
      }
      this.activeHeading = id;
      this.updateMarkerPosition(activeLink);
    }
  }

  setupSmoothScroll() {
    this.tocContainer.addEventListener('click', (e) => {
      if (e.target.classList.contains('toc-link')) {
        e.preventDefault();

        const targetId = e.target.getAttribute('href').substring(1);
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          const offsetTop = targetElement.offsetTop - 100; // Account for fixed header

          window.scrollTo({
            top: offsetTop,
            behavior: 'smooth'
          });

          // Update URL without scrolling
          history.pushState(null, null, `#${targetId}`);
        }
      }
    });
  }
}

// Initialize TOC when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new TableOfContents();
  });
} else {
  new TableOfContents();
}

export default TableOfContents;
